import useMotionTrackingEntitlement, { clearMotionTrackingSession } from "../hooks/useMotionTrackingEntitlement";
// /home/ubuntu/avaai_dashboard/frontend/src/components/MobileOrbMenu.tsx

import { useContext, useEffect, useRef, useState } from "react";
import { ThemeContext } from "../context/ThemeContext";
import {
  usePreferences,
} from "../context/PreferencesContext";
import {
  getAvaaioUiCopy,
} from "../utils/avaaioUiCopy";
import "../styles/MobileOrbMenu.css";

type ThemeName = string;

const THEME_ORDER: ThemeName[] = [
  "cyan",
  "purple",
  "emerald",
  "amber",
  "lava",
  "midnight",
  "aurora",
];


function emit(name: string) {
  window.dispatchEvent(new CustomEvent(name));
}

type OrbPos = {
  x: number;
  y: number;
};

/*
 * AVAAIO MOBILE UX PHASE 3A
 *
 * V2 intentionally establishes the new lower-right home position
 * without inheriting an obsolete V1 coordinate from the previous
 * mobile layout.
 */
const LS_ORB_POS = "ava_mobile_orb_position_v2";

const ORB_SIZE = 78;
const ORB_MARGIN = 14;
const ORB_DOCK_GAP = 14;
const ORB_COLLISION_GAP = 14;
const ORB_CLEAR_DELAY_MS = 450;

type OrbViewportBounds = {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
};

type OrbRectLike = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

type SafeAreaInsets = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

let safeAreaCacheKey = "";
let safeAreaCache: SafeAreaInsets = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function numericCssValue(value: string | null | undefined) {
  const parsed = Number.parseFloat(value || "0");
  return Number.isFinite(parsed) ? parsed : 0;
}

function getSafeAreaInsets(): SafeAreaInsets {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    !document.body
  ) {
    return safeAreaCache;
  }

  const cacheKey =
    `${window.innerWidth}x${window.innerHeight}`;

  if (cacheKey === safeAreaCacheKey) {
    return safeAreaCache;
  }

  const probe = document.createElement("div");

  probe.setAttribute("aria-hidden", "true");

  probe.style.cssText = [
    "position:fixed",
    "visibility:hidden",
    "pointer-events:none",
    "width:0",
    "height:0",
    "padding-top:env(safe-area-inset-top)",
    "padding-right:env(safe-area-inset-right)",
    "padding-bottom:env(safe-area-inset-bottom)",
    "padding-left:env(safe-area-inset-left)",
  ].join(";");

  document.body.appendChild(probe);

  const computed = window.getComputedStyle(probe);

  safeAreaCache = {
    top: numericCssValue(computed.paddingTop),
    right: numericCssValue(computed.paddingRight),
    bottom: numericCssValue(computed.paddingBottom),
    left: numericCssValue(computed.paddingLeft),
  };

  safeAreaCacheKey = cacheKey;
  probe.remove();

  return safeAreaCache;
}

function getOrbViewportBounds(): OrbViewportBounds {
  if (typeof window === "undefined") {
    return {
      left: 0,
      top: 0,
      right: 390,
      bottom: 844,
      width: 390,
      height: 844,
    };
  }

  const visualViewport = window.visualViewport;

  const left =
    visualViewport?.offsetLeft || 0;

  const top =
    visualViewport?.offsetTop || 0;

  const width =
    visualViewport?.width || window.innerWidth;

  const height =
    visualViewport?.height || window.innerHeight;

  return {
    left,
    top,
    right: left + width,
    bottom: top + height,
    width,
    height,
  };
}

function hasSavedOrbPosition() {
  try {
    return localStorage.getItem(LS_ORB_POS) !== null;
  } catch {
    return false;
  }
}

function getVisibleChatDockTop(
  bounds: OrbViewportBounds
): number | null {
  if (typeof document === "undefined") {
    return null;
  }

  /*
   * AVAAIO MOBILE UX PHASE 4F
   *
   * `.chat-dock` itself covers the complete viewport, so it
   * must never be treated as the visible bottom control.
   * Inspect its visible children and the real holo console.
   */
  const elements = Array.from(
    document.querySelectorAll<HTMLElement>(
      ".holo-console, .chat-dock > *"
    )
  );

  const candidateTops =
    elements
      .map(element => {
        const style =
          window.getComputedStyle(
            element
          );

        const rect =
          element.getBoundingClientRect();

        const visible =
          style.display !== "none" &&
          style.visibility !== "hidden" &&
          numericCssValue(
            style.opacity
          ) !== 0 &&
          rect.width > 1 &&
          rect.height > 1 &&
          rect.bottom > bounds.top &&
          rect.top < bounds.bottom;

        if (!visible) {
          return null;
        }

        /*
         * Exclude full-screen overlay containers. A genuine
         * dock/control occupies the lower portion of the view.
         */
        const lowerScreenControl =
          rect.top >
            bounds.top +
            Math.min(
              130,
              bounds.height * 0.30
            ) &&
          rect.bottom >
            bounds.top +
            bounds.height * 0.52 &&
          rect.height <
            bounds.height * 0.72;

        return lowerScreenControl
          ? rect.top
          : null;
      })
      .filter(
        (
          value
        ): value is number =>
          typeof value === "number" &&
          Number.isFinite(value)
      );

  if (candidateTops.length === 0) {
    return null;
  }

  return Math.min(
    ...candidateTops
  );
}

function normalizePos(raw: OrbPos): OrbPos {
  if (typeof window === "undefined") {
    return raw;
  }

  const bounds =
    getOrbViewportBounds();

  const safeArea =
    getSafeAreaInsets();

  const minX =
    bounds.left +
    Math.max(
      ORB_MARGIN,
      safeArea.left + 6
    );

  const maxX =
    bounds.right -
    ORB_SIZE -
    Math.max(
      ORB_MARGIN,
      safeArea.right + 6
    );

  const minY =
    bounds.top +
    Math.max(
      ORB_MARGIN,
      safeArea.top + 6
    );

  const viewportMaxY =
    bounds.bottom -
    ORB_SIZE -
    Math.max(
      ORB_MARGIN,
      safeArea.bottom + 6
    );

  const dockTop =
    getVisibleChatDockTop(
      bounds
    );

  const dockMaxY =
    dockTop !== null
      ? dockTop -
        ORB_SIZE -
        ORB_DOCK_GAP
      : viewportMaxY;

  const maxY =
    Math.min(
      viewportMaxY,
      dockMaxY
    );

  return {
    x: clamp(
      raw.x,
      minX,
      Math.max(
        minX,
        maxX
      )
    ),

    y: clamp(
      raw.y,
      minY,
      Math.max(
        minY,
        maxY
      )
    ),
  };
}

function getDefaultPos(): OrbPos {
  if (typeof window === "undefined") {
    return { x: 18, y: 18 };
  }

  const bounds = getOrbViewportBounds();
  const safeArea = getSafeAreaInsets();

  const rightMargin =
    Math.max(ORB_MARGIN, safeArea.right + 6);

  const dockTop =
    getVisibleChatDockTop(bounds);

  const isPortrait =
    bounds.height >= bounds.width;

  const fallbackBottomClearance =
    isPortrait ? 138 : 108;

  const preferredY =
    dockTop !== null
      ? dockTop - ORB_SIZE - ORB_DOCK_GAP
      : bounds.bottom -
        ORB_SIZE -
        fallbackBottomClearance;

  return normalizePos({
    x:
      bounds.right -
      ORB_SIZE -
      rightMargin,
    y: preferredY,
  });
}

function getOrbRect(pos: OrbPos): OrbRectLike {
  return {
    left: pos.x,
    top: pos.y,
    right: pos.x + ORB_SIZE,
    bottom: pos.y + ORB_SIZE,
  };
}

function rectsOverlap(
  first: OrbRectLike,
  second: OrbRectLike,
  gap = 0
) {
  return (
    first.left < second.right + gap &&
    first.right > second.left - gap &&
    first.top < second.bottom + gap &&
    first.bottom > second.top - gap
  );
}

/*
 * AVAAIO MOBILE UX PHASE 4F
 *
 * Collision obstacles that can occupy useful orb space.
 * Regular users see the live Vision preview. Admin users may
 * additionally see the protected diagnostic HUD.
 */
function getVisibleOrbObstacles(): HTMLElement[] {
  if (typeof document === "undefined") {
    return [];
  }

  const bounds =
    getOrbViewportBounds();

  const selectors = [
    ".ava-vision-live-preview",
    ".ava-vision-user-hud",
    ".ava-vision-admin-hud",
    ".ava-browser-panel",
  ];

  const seen =
    new Set<HTMLElement>();

  const visible:
    HTMLElement[] = [];

  for (const selector of selectors) {
    const elements =
      document.querySelectorAll<HTMLElement>(
        selector
      );

    elements.forEach(element => {
      if (seen.has(element)) {
        return;
      }

      seen.add(element);

      const style =
        window.getComputedStyle(
          element
        );

      const rect =
        element.getBoundingClientRect();

      const onScreen =
        rect.right > bounds.left &&
        rect.left < bounds.right &&
        rect.bottom > bounds.top &&
        rect.top < bounds.bottom;

      /*
       * Ignore invisible and full-screen layout containers.
       * Only actual cards/panels should displace the orb.
       */
      const genuineObstacle =
        style.display !== "none" &&
        style.visibility !== "hidden" &&
        numericCssValue(
          style.opacity
        ) !== 0 &&
        rect.width > 1 &&
        rect.height > 1 &&
        onScreen &&
        (
          rect.width <
            bounds.width * 0.96 ||
          rect.height <
            bounds.height * 0.96
        );

      if (genuineObstacle) {
        visible.push(element);
      }
    });
  }

  return visible;
}

function getVisibleOrbObstacleRects(): OrbRectLike[] {
  return getVisibleOrbObstacles()
    .map(element => {
      const rect =
        element.getBoundingClientRect();

      return {
        left: rect.left,
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
      };
    });
}

function orbPositionIsBlocked(
  position: OrbPos,
  obstacles: OrbRectLike[]
) {
  const orbRect =
    getOrbRect(position);

  return obstacles.some(
    obstacle =>
      rectsOverlap(
        orbRect,
        obstacle,
        ORB_COLLISION_GAP
      )
  );
}

function orbPositionDistance(
  first: OrbPos,
  second: OrbPos
) {
  const dx =
    first.x - second.x;

  const dy =
    first.y - second.y;

  return (
    dx * dx +
    dy * dy
  );
}

/*
 * Choose the nearest valid position instead of always forcing
 * the orb to the opposite side. This minimizes distracting
 * movement on phones, tablets, foldables, and resized windows.
 */
function getNearestSafeOrbPos(
  home: OrbPos,
  obstacles: OrbRectLike[]
): OrbPos {
  const preferred =
    normalizePos(home);

  if (
    !orbPositionIsBlocked(
      preferred,
      obstacles
    )
  ) {
    return preferred;
  }

  const bounds =
    getOrbViewportBounds();

  const safeArea =
    getSafeAreaInsets();

  const leftX =
    bounds.left +
    Math.max(
      ORB_MARGIN,
      safeArea.left + 6
    );

  const rightX =
    bounds.right -
    ORB_SIZE -
    Math.max(
      ORB_MARGIN,
      safeArea.right + 6
    );

  const topY =
    bounds.top +
    Math.max(
      ORB_MARGIN,
      safeArea.top + 6
    );

  const bottomY =
    normalizePos({
      x: preferred.x,
      y: bounds.bottom,
    }).y;

  const candidates:
    OrbPos[] = [];

  const candidateKeys =
    new Set<string>();

  const addCandidate = (
    raw: OrbPos
  ) => {
    const position =
      normalizePos(raw);

    const key =
      `${Math.round(position.x)}:` +
      `${Math.round(position.y)}`;

    if (candidateKeys.has(key)) {
      return;
    }

    candidateKeys.add(key);
    candidates.push(position);
  };

  addCandidate(preferred);
  addCandidate(getDefaultPos());

  addCandidate({
    x: leftX,
    y: preferred.y,
  });

  addCandidate({
    x: rightX,
    y: preferred.y,
  });

  addCandidate({
    x: leftX,
    y: topY,
  });

  addCandidate({
    x: rightX,
    y: topY,
  });

  addCandidate({
    x: leftX,
    y: bottomY,
  });

  addCandidate({
    x: rightX,
    y: bottomY,
  });

  /*
   * Generate positions beside and above/below every visible
   * obstacle. These candidates work regardless of whether a
   * device is portrait, landscape, split-screen, or folded.
   */
  for (const obstacle of obstacles) {
    const obstacleCenterX =
      (
        obstacle.left +
        obstacle.right
      ) / 2;

    const obstacleCenterY =
      (
        obstacle.top +
        obstacle.bottom
      ) / 2;

    const besideLeft =
      obstacle.left -
      ORB_SIZE -
      ORB_COLLISION_GAP;

    const besideRight =
      obstacle.right +
      ORB_COLLISION_GAP;

    const above =
      obstacle.top -
      ORB_SIZE -
      ORB_COLLISION_GAP;

    const below =
      obstacle.bottom +
      ORB_COLLISION_GAP;

    addCandidate({
      x: besideLeft,
      y: preferred.y,
    });

    addCandidate({
      x: besideRight,
      y: preferred.y,
    });

    addCandidate({
      x:
        obstacleCenterX -
        ORB_SIZE / 2,
      y: above,
    });

    addCandidate({
      x:
        obstacleCenterX -
        ORB_SIZE / 2,
      y: below,
    });

    addCandidate({
      x: besideLeft,
      y:
        obstacleCenterY -
        ORB_SIZE / 2,
    });

    addCandidate({
      x: besideRight,
      y:
        obstacleCenterY -
        ORB_SIZE / 2,
    });

    addCandidate({
      x: besideLeft,
      y: above,
    });

    addCandidate({
      x: besideRight,
      y: above,
    });

    addCandidate({
      x: besideLeft,
      y: below,
    });

    addCandidate({
      x: besideRight,
      y: below,
    });
  }

  /*
   * AVAAIO ORB FREE-CORNER PRIORITY
   *
   * Automatic placement prefers predictable corners before
   * using obstacle-adjacent positions:
   *
   * 1. bottom-right
   * 2. bottom-left
   * 3. top-right
   * 4. top-left
   *
   * Every candidate still passes through normalizePos(),
   * safe-area bounds, visible-viewport bounds, and collision
   * detection before being selected.
   */
  const cornerCandidates: OrbPos[] = [
    normalizePos({
      x: rightX,
      y: bottomY,
    }),
    normalizePos({
      x: leftX,
      y: bottomY,
    }),
    normalizePos({
      x: rightX,
      y: topY,
    }),
    normalizePos({
      x: leftX,
      y: topY,
    }),
  ];

  for (const corner of cornerCandidates) {
    if (
      !orbPositionIsBlocked(
        corner,
        obstacles
      )
    ) {
      return corner;
    }
  }

  /*
   * If every corner is occupied, preserve the existing
   * intelligent nearest-safe-position fallback.
   */
  const validCandidates =
    candidates
      .filter(
        position =>
          !orbPositionIsBlocked(
            position,
            obstacles
          )
      )
      .sort(
        (first, second) =>
          orbPositionDistance(
            first,
            preferred
          ) -
          orbPositionDistance(
            second,
            preferred
          )
      );

  if (validCandidates.length > 0) {
    return validCandidates[0];
  }

  /*
   * Narrow split-screen and foldable fallback. Search the
   * available safe region on a coarse grid only when the
   * normal nearby candidates are all occupied.
   */
  const searchStep =
    Math.max(
      28,
      Math.round(
        ORB_SIZE * 0.48
      )
    );

  const gridCandidates:
    OrbPos[] = [];

  for (
    let y = topY;
    y <= bottomY;
    y += searchStep
  ) {
    for (
      let x = leftX;
      x <= rightX;
      x += searchStep
    ) {
      const position =
        normalizePos({
          x,
          y,
        });

      if (
        !orbPositionIsBlocked(
          position,
          obstacles
        )
      ) {
        gridCandidates.push(
          position
        );
      }
    }
  }

  gridCandidates.sort(
    (first, second) =>
      orbPositionDistance(
        first,
        preferred
      ) -
      orbPositionDistance(
        second,
        preferred
      )
  );

  return (
    gridCandidates[0] ||
    preferred
  );
}

function loadSavedPos(): OrbPos {
  try {
    const raw = localStorage.getItem(LS_ORB_POS);
    if (!raw) return getDefaultPos();
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x === "number" && typeof parsed?.y === "number") {
      return normalizePos(parsed);
    }
  } catch {}
  return getDefaultPos();
}

function savePos(pos: OrbPos) {
  try {
    localStorage.setItem(LS_ORB_POS, JSON.stringify(pos));
  } catch {}
}

function themeLabel(theme: ThemeName) {
  switch (theme) {
    case "cyan":
      return "Cyan";

    case "purple":
      return "Purple";

    case "emerald":
      return "Emerald";

    case "amber":
      return "Amber";

    case "lava":
      return "Lava";

    case "midnight":
      return "Midnight";

      case "aurora":
      return "Aurora";

    default:
      return theme;
  }
}

export default function MobileOrbMenu() {
  
  const [orbEyeOpen, setOrbEyeOpen] = useState<boolean>(() => {
    try {
      // Ava Vision must always start OFF.
      // Never restore previous camera/eye state automatically.
      return false;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const syncEye = (event: Event) => {
      const custom = event as CustomEvent<{ open?: boolean }>;
      const next = !!custom?.detail?.open;
      setOrbEyeOpen(next);
      try {
        localStorage.setItem("ava_eye_open", next ? "1" : "0");
      } catch {}
    };

    window.addEventListener("ava-eye-toggle", syncEye as EventListener);

    return () => window.removeEventListener("ava-eye-toggle", syncEye as EventListener);
  }, []);

  const toggleOrbEye = () => {
    const next = !orbEyeOpen;
    setOrbEyeOpen(next);

    try {
      localStorage.setItem("ava_eye_open", next ? "1" : "0");
    } catch {}

    window.dispatchEvent(new Event("ava_interaction"));
    window.dispatchEvent(
      new CustomEvent("ava-eye-toggle", {
        detail: { open: next, source: "orb-menu", ts: Date.now() },
      })
    );
    window.dispatchEvent(new Event("ava_face_cam_tracking_changed"));
  };


  // =====================================================
  // AVA MOTION TRACK
  //
  // MobileOrbMenu owns only the displayed control state.
  // ChatBox owns permission handling and activation.
  // AvaFace owns the actual motion tracking.
  // =====================================================

  const {
    motionTrackingAllowed,
  } = useMotionTrackingEntitlement();

  const [orbMotionEnabled, setOrbMotionEnabled] =
    useState<boolean>(false);

  useEffect(() => {
    if (motionTrackingAllowed) return;

    clearMotionTrackingSession();
    setOrbMotionEnabled(false);
  }, [motionTrackingAllowed]);

  useEffect(() => {
    const syncMotionTrack = (event: Event) => {
      const custom = event as CustomEvent<{
        enabled?: boolean;
      }>;

      const requested =
        Boolean(custom?.detail?.enabled);

      const next =
        requested && motionTrackingAllowed;

      if (requested && !motionTrackingAllowed) {
        console.warn(
          "[AVA MOTION] Orb state rejected by entitlement."
        );

        clearMotionTrackingSession();
      }

      setOrbMotionEnabled(next);

      try {
        sessionStorage.setItem(
          "ava_device_motion_look",
          next ? "1" : "0"
        );
      } catch {}
    };

    window.addEventListener(
      "ava-device-motion-toggle",
      syncMotionTrack as EventListener
    );

    return () => {
      window.removeEventListener(
        "ava-device-motion-toggle",
        syncMotionTrack as EventListener
      );
    };
  }, [motionTrackingAllowed]);

  const toggleOrbMotionTrack = async () => {
    window.dispatchEvent(new Event("ava_interaction"));

    const nextEnabled = !orbMotionEnabled;

    if (nextEnabled && !motionTrackingAllowed) {
      clearMotionTrackingSession();
      setOrbMotionEnabled(false);

      window.alert(
        interfaceCopy.mobile.motionPaidAlert
      );

      return;
    }

    // Apple requires this request to happen directly inside
    // the original button click / transient user activation.
    if (nextEnabled) {
      const OrientationEvent =
        (window as any).DeviceOrientationEvent;

      if (!OrientationEvent) {
        window.alert(
          "Ava Motion Track is not available in this browser or app view."
        );

        return;
      }

      let permissionGranted = false;

      try {
        permissionGranted =
          sessionStorage.getItem(
            "ava_device_motion_permission_granted"
          ) === "1";
      } catch {}

      if (
        typeof OrientationEvent.requestPermission ===
          "function" &&
        !permissionGranted
      ) {
        try {
          const permission =
            await OrientationEvent.requestPermission();

          if (permission !== "granted") {
            setOrbMotionEnabled(false);

            try {
              sessionStorage.setItem(
                "ava_device_motion_look",
                "0"
              );
            } catch {}

            window.alert(
              "Ava Motion Track remains off. Allow Motion & Orientation Access, then try again."
            );

            return;
          }

          try {
            sessionStorage.setItem(
              "ava_device_motion_permission_granted",
              "1"
            );
          } catch {}
        } catch (error) {
          console.warn(
            "[AVA MOTION] Direct permission request failed",
            error
          );

          setOrbMotionEnabled(false);

          window.alert(
            "Ava could not open the Motion & Orientation permission request."
          );

          return;
        }
      }
    }

    window.dispatchEvent(
      new CustomEvent("ava-mobile-motion-toggle", {
        detail: {
          enabled: nextEnabled,
          permissionGranted: nextEnabled,
          source: "orb-menu",
          ts: Date.now(),
        },
      })
    );
  };


  const [menuChatPlan, setMenuChatPlan] = useState<string>(() => {
    try {
      return localStorage.getItem("ava_chat_plan") || "free";
    } catch {
      return "free";
    }
  });

  const [menuChatLock, setMenuChatLock] = useState<any>(() => {
    try {
      return JSON.parse(localStorage.getItem("ava_chat_lock") || "{}");
    } catch {
      return {};
    }
  });


  useEffect(() => {
    try {
      const rawPlan =
        String(localStorage.getItem("ava_chat_plan") || "free")
          .trim()
          .toLowerCase();

      const rawRole =
        String(localStorage.getItem("ava_user_role") || "user")
          .trim()
          .toLowerCase();

      const sub =
        String(localStorage.getItem("subscription_status") || "inactive")
          .trim()
          .toLowerCase();

      const isAdmin =
        rawRole === "admin";

      const isPro =
        sub === "active" ||
        rawPlan === "pro" ||
        rawRole === "vip";

      const sanitized =
        isAdmin
          ? "admin"
          : isPro
            ? "pro"
            : "free";

      setMenuChatPlan(sanitized);

      localStorage.setItem("ava_chat_plan", sanitized);

      console.log("[AvaVision] sanitized plan =", sanitized);

    } catch (e) {
      console.warn("[AvaVision] sanitizer failed:", e);
    }
  }, []);

  useEffect(() => {
    const syncLock = (event?: Event) => {
      try {
        const custom = event as CustomEvent<{ plan?: string; lock?: any }>;
        if (custom?.detail?.plan) {
          setMenuChatPlan(String(custom.detail.plan || "free"));
        } else {
          setMenuChatPlan(localStorage.getItem("ava_chat_plan") || "free");
        }

        if (typeof custom?.detail?.lock !== "undefined") {
          setMenuChatLock(custom.detail.lock || {});
        } else {
          setMenuChatLock(JSON.parse(localStorage.getItem("ava_chat_lock") || "{}"));
        }
      } catch {
        try {
          setMenuChatPlan(localStorage.getItem("ava_chat_plan") || "free");
          setMenuChatLock(JSON.parse(localStorage.getItem("ava_chat_lock") || "{}"));
        } catch {}
      }
    };

    window.addEventListener("ava_chat_lock_changed", syncLock as EventListener);
    window.addEventListener("storage", syncLock as EventListener);
    return () => {
      window.removeEventListener("ava_chat_lock_changed", syncLock as EventListener);
      window.removeEventListener("storage", syncLock as EventListener);
    };
  }, []);

  const isFreePlanUser = String(menuChatPlan || "").toLowerCase() === "free";

  const isLockedFreeUser =
    isFreePlanUser &&
    Boolean(menuChatLock?.limit_reached);
  const viewportW =
    typeof window !== "undefined" ? window.innerWidth : 390;

  const viewportH =
    typeof window !== "undefined" ? window.innerHeight : 844;

  const isLandscape = viewportW > viewportH;

  const isPhone =
    viewportW <= 640;

  const isCompactLandscape =
    isLandscape &&
    viewportH <= 500;


  const goProfile = () => {
    window.location.href = "/profile";
    closeMenu();
  };



  const runQuickCommand = (cmd: string) => {
    try {
      window.dispatchEvent(
        new CustomEvent("ava-orb-command", {
          detail: { command: cmd },
        })
      );
    } catch {}

    closeMenu();
  };

  const goUpgrade = () => {
    window.location.href = "/settings";
    closeMenu();
  };

  const lockNotice = () => {
    window.dispatchEvent(new Event("ava_interaction"));
    try {
      alert("This control is locked on the Free plan. Upgrade to Pro to unlock more Ava controls.");
    } catch {}
  };


// === LIVE THEME SYSTEM ===
const { theme, setTheme } = useContext(ThemeContext);
  const { preferences } = usePreferences();
  const interfaceCopy = getAvaaioUiCopy(
    preferences.language
  );


  const [open, setOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);

  const [showOrbDiscoveryHint, setShowOrbDiscoveryHint] =
    useState<boolean>(() => {
      try {
        return localStorage.getItem("ava_orb_menu_discovered") === "0";
      } catch {
        return false;
      }
    });

  // =========================================
  // REACTIVE ORB STATES
  // =========================================

  const [orbSpeaking, setOrbSpeaking] = useState(false);
  const [orbThinking, setOrbThinking] = useState(false);
  const [orbListening, setOrbListening] = useState(false);
  const [pos, setPos] = useState<OrbPos>(() => loadSavedPos());

  const posRef = useRef(pos);

  // AVAAIO MOBILE UX PHASE 3A:
  // preferredPosRef is the user's home position. Automatic Vision
  // avoidance changes the rendered position without overwriting home.
  const preferredPosRef = useRef<OrbPos>(pos);
  const userPositionedRef = useRef<boolean>(
    hasSavedOrbPosition()
  );
  const autoRelocatedRef = useRef(false);
  const visionClearTimerRef =
    useRef<number | null>(null);

  const dragRef = useRef<{
    active: boolean;
    moved: boolean;
    startX: number;
    startY: number;
    offsetX: number;
    offsetY: number;
  }>({
    active: false,
    moved: false,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
  });

  useEffect(() => {
    posRef.current = pos;
  }, [pos]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      setOpen(false);
      setThemeOpen(false);
    }, 9000);
    return () => clearTimeout(timer);
  }, [open, themeOpen]);

  useEffect(() => {
    let resizeRaf = 0;

    const onResize = () => {
      cancelAnimationFrame(resizeRaf);

      resizeRaf = requestAnimationFrame(() => {
        if (dragRef.current.active) return;

        const preferred =
          userPositionedRef.current
            ? normalizePos(preferredPosRef.current)
            : getDefaultPos();

        preferredPosRef.current = preferred;

        if (userPositionedRef.current) {
          savePos(preferred);
        }

        if (!autoRelocatedRef.current) {
          posRef.current = preferred;
          setPos(preferred);
        }
      });
    };

    onResize();

    window.addEventListener("resize", onResize);
    window.addEventListener(
      "orientationchange",
      onResize
    );

    window.visualViewport?.addEventListener(
      "resize",
      onResize
    );

    window.visualViewport?.addEventListener(
      "scroll",
      onResize
    );

    return () => {
      cancelAnimationFrame(resizeRaf);

      window.removeEventListener("resize", onResize);
      window.removeEventListener(
        "orientationchange",
        onResize
      );

      window.visualViewport?.removeEventListener(
        "resize",
        onResize
      );

      window.visualViewport?.removeEventListener(
        "scroll",
        onResize
      );
    };
  }, []);

  /*
   * AVAAIO MOBILE UX PHASE 4F
   *
   * All-device responsive orb collision authority.
   *
   * - Preserves the user's preferred/saved position.
   * - Moves only when the preferred position is blocked.
   * - Chooses the nearest safe position.
   * - Avoids Vision preview, admin HUD, browser panels,
   *   chat controls, safe areas, display cutouts, and keyboard.
   * - Re-evaluates after rotation, resize, viewport movement,
   *   DOM changes, and measured obstacle-size changes.
   */
  useEffect(() => {
    let evaluationRaf = 0;
    let evaluationInterval = 0;

    let resizeObserver:
      ResizeObserver | null = null;

    const clearReturnTimer = () => {
      if (
        visionClearTimerRef.current !==
        null
      ) {
        window.clearTimeout(
          visionClearTimerRef.current
        );

        visionClearTimerRef.current =
          null;
      }
    };

    const applyPosition = (
      next: OrbPos
    ) => {
      const current =
        posRef.current;

      /*
       * Avoid unnecessary React updates when viewport geometry
       * changes by less than one physical CSS pixel.
       */
      if (
        Math.abs(
          current.x - next.x
        ) < 1 &&
        Math.abs(
          current.y - next.y
        ) < 1
      ) {
        return;
      }

      posRef.current = next;
      setPos(next);
    };

    const getPreferred = () => {
      const preferred =
        userPositionedRef.current
          ? normalizePos(
              preferredPosRef.current
            )
          : getDefaultPos();

      preferredPosRef.current =
        preferred;

      return preferred;
    };

    const preferredAreaIsBlocked = (
      preferred: OrbPos,
      obstacles: OrbRectLike[]
    ) =>
      orbPositionIsBlocked(
        preferred,
        obstacles
      );

    const scheduleReturnHome = () => {
      if (
        visionClearTimerRef.current !==
        null
      ) {
        return;
      }

      visionClearTimerRef.current =
        window.setTimeout(() => {
          visionClearTimerRef.current =
            null;

          if (
            dragRef.current.active
          ) {
            return;
          }

          const preferred =
            getPreferred();

          const obstacles =
            getVisibleOrbObstacleRects();

          if (
            preferredAreaIsBlocked(
              preferred,
              obstacles
            )
          ) {
            return;
          }

          autoRelocatedRef.current =
            false;

          applyPosition(
            preferred
          );
        }, ORB_CLEAR_DELAY_MS);
    };

    const evaluateLayout = () => {
      if (
        dragRef.current.active
      ) {
        return;
      }

      /*
       * AVAAIO_ORB_MANUAL_POSITION_PRIORITY_V1
       *
       * Once the user manually places the mobile sphere,
       * that saved position owns the final layout.
       *
       * Automatic collision relocation remains enabled only
       * while the sphere is using its default home position.
       * Resize and orientation handling may still normalize
       * the user's position so it remains inside the visible
       * viewport and device safe areas.
       */
      if (
        userPositionedRef.current
      ) {
        clearReturnTimer();

        autoRelocatedRef.current =
          false;

        const userPreferred =
          getPreferred();

        applyPosition(
          userPreferred
        );

        return;
      }

      const preferred =
        getPreferred();

      const obstacles =
        getVisibleOrbObstacleRects();

      const homeBlocked =
        preferredAreaIsBlocked(
          preferred,
          obstacles
        );

      if (homeBlocked) {
        clearReturnTimer();

        autoRelocatedRef.current =
          true;

        applyPosition(
          getNearestSafeOrbPos(
            preferred,
            obstacles
          )
        );

        return;
      }

      if (
        autoRelocatedRef.current
      ) {
        scheduleReturnHome();
      } else if (
        !userPositionedRef.current
      ) {
        applyPosition(
          preferred
        );
      }
    };

    const refreshResizeTargets = () => {
      if (!resizeObserver) {
        return;
      }

      resizeObserver.disconnect();

      getVisibleOrbObstacles()
        .forEach(element => {
          resizeObserver?.observe(
            element
          );
        });

      document
        .querySelectorAll<HTMLElement>(
          ".holo-console, .chat-dock > *"
        )
        .forEach(element => {
          resizeObserver?.observe(
            element
          );
        });
    };

    const scheduleEvaluation = () => {
      cancelAnimationFrame(
        evaluationRaf
      );

      evaluationRaf =
        requestAnimationFrame(() => {
          refreshResizeTargets();
          evaluateLayout();
        });
    };

    if (
      typeof ResizeObserver !==
      "undefined"
    ) {
      resizeObserver =
        new ResizeObserver(
          scheduleEvaluation
        );
    }

    const mutationObserver =
      new MutationObserver(
        scheduleEvaluation
      );

    mutationObserver.observe(
      document.body,
      {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          "class",
          "style",
          "data-status",
          "aria-hidden",
        ],
      }
    );

    /*
     * Low-frequency geometry fallback for browser UI movement
     * that may not emit a reliable resize event.
     */
    evaluationInterval =
      window.setInterval(
        scheduleEvaluation,
        320
      );

    window.addEventListener(
      "resize",
      scheduleEvaluation
    );

    window.addEventListener(
      "orientationchange",
      scheduleEvaluation
    );

    window.visualViewport?.addEventListener(
      "resize",
      scheduleEvaluation
    );

    window.visualViewport?.addEventListener(
      "scroll",
      scheduleEvaluation
    );

    window.addEventListener(
      "ava-eye-toggle",
      scheduleEvaluation as EventListener
    );

    scheduleEvaluation();

    return () => {
      cancelAnimationFrame(
        evaluationRaf
      );

      clearReturnTimer();

      window.clearInterval(
        evaluationInterval
      );

      mutationObserver.disconnect();
      resizeObserver?.disconnect();

      window.removeEventListener(
        "resize",
        scheduleEvaluation
      );

      window.removeEventListener(
        "orientationchange",
        scheduleEvaluation
      );

      window.visualViewport?.removeEventListener(
        "resize",
        scheduleEvaluation
      );

      window.visualViewport?.removeEventListener(
        "scroll",
        scheduleEvaluation
      );

      window.removeEventListener(
        "ava-eye-toggle",
        scheduleEvaluation as EventListener
      );
    };
  }, []);

  const closeMenu = () => {
    setOpen(false);
    setThemeOpen(false);
  };

  const startDrag = (clientX: number, clientY: number) => {
    if (visionClearTimerRef.current !== null) {
      window.clearTimeout(
        visionClearTimerRef.current
      );

      visionClearTimerRef.current = null;
    }

    autoRelocatedRef.current = false;

    dragRef.current.active = true;
    dragRef.current.moved = false;
    dragRef.current.startX = clientX;
    dragRef.current.startY = clientY;
    dragRef.current.offsetX = clientX - posRef.current.x;
    dragRef.current.offsetY = clientY - posRef.current.y;
    document.body.classList.add("ava-orb-dragging");
  };

  const moveDrag = (clientX: number, clientY: number) => {
    if (!dragRef.current.active) return;

    const dx = Math.abs(clientX - dragRef.current.startX);
    const dy = Math.abs(clientY - dragRef.current.startY);
    if (dx > 6 || dy > 6) dragRef.current.moved = true;

    const orbSize = 76;
    const margin = 12;

    const nextX = clamp(
      clientX - dragRef.current.offsetX,
      margin,
      Math.max(margin, window.innerWidth - orbSize - margin)
    );

    const nextY = clamp(
      clientY - dragRef.current.offsetY,
      margin,
      Math.max(margin, window.innerHeight - orbSize - margin)
    );

    // Instant drag tracking: no delayed catch-up.
    const next = {
      x: nextX,
      y: nextY,
    };

    posRef.current = next;
    setPos(next);
  };

  const endDrag = () => {
    if (!dragRef.current.active) return;

    dragRef.current.active = false;
    document.body.classList.remove("ava-orb-dragging");

    const preferred =
      normalizePos(posRef.current);

    userPositionedRef.current = true;
    preferredPosRef.current = preferred;
    autoRelocatedRef.current = false;

    posRef.current = preferred;
    setPos(preferred);
    savePos(preferred);
  };

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => moveDrag(e.clientX, e.clientY);
    const onMouseUp = () => endDrag();

    const onTouchMove = (e: TouchEvent) => {
      if (dragRef.current.active) e.preventDefault();
      const t = e.touches[0];
      if (!t) return;
      moveDrag(t.clientX, t.clientY);
    };

    const onTouchEnd = () => endDrag();

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd);
    window.addEventListener("touchcancel", onTouchEnd);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []);

  const handleOrbClick = () => {
    if (dragRef.current.moved) return;

    if (showOrbDiscoveryHint) {
      setShowOrbDiscoveryHint(false);
      try {
        localStorage.setItem("ava_orb_menu_discovered", "1");
      } catch {}
    }

    setOpen((prev) => {
      const next = !prev;
      if (!next) setThemeOpen(false);
      return next;
    });
  };

  const selectTheme = (nextTheme: ThemeName) => {
    setTheme(nextTheme as any);
    setThemeOpen(false);

    try {
      window.dispatchEvent(new Event("ava_interaction"));
    } catch {}
  };

  const runOrbAction = (event: any, action: () => void) => {
    try {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      clearInactiveTimer();
    } catch {}
    action();
  };

  const orbWrapRef = useRef<HTMLDivElement | null>(null);
  const inactiveTimerRef = useRef<number | null>(null);

  const clearInactiveTimer = () => {
    if (inactiveTimerRef.current) {
      window.clearTimeout(inactiveTimerRef.current);
      inactiveTimerRef.current = null;
    }
  };

  const startInactiveTimer = () => {
    clearInactiveTimer();
    // Auto-close disabled:
    // Mobile orb now stays open until the user manually closes it.
    inactiveTimerRef.current = null;
  };



  useEffect(() => {
    if (!open) {
      clearInactiveTimer();
      return;
    }

    startInactiveTimer();

    const handlePointerOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (orbWrapRef.current && !orbWrapRef.current.contains(target)) {
        setOpen(false);
        setThemeOpen(false);
        clearInactiveTimer();
      }
    };

    const handleWindowBlur = () => {
      setOpen(false);
      setThemeOpen(false);
      clearInactiveTimer();
    };

    document.addEventListener("mousedown", handlePointerOutside);
    document.addEventListener("touchstart", handlePointerOutside, { passive: true });
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      document.removeEventListener("mousedown", handlePointerOutside);
      document.removeEventListener("touchstart", handlePointerOutside);
      window.removeEventListener("blur", handleWindowBlur);
      clearInactiveTimer();
    };
  }, [open]);

  const menuStyle = {
    left: `${Math.max(
      8,
      pos.x - (isCompactLandscape ? 132 : isPhone ? 150 : 170)
    )}px`,

    top: `${Math.max(
      8,
      pos.y - (
        isCompactLandscape
          ? (themeOpen ? 250 : 168)
          : themeOpen
            ? 340
            : 220
      )
    )}px`,

    width: isCompactLandscape
      ? "min(78vw, 240px)"
      : isPhone
        ? "min(86vw, 290px)"
        : "min(92vw, 340px)",

    maxWidth: isCompactLandscape
      ? "240px"
      : isPhone
        ? "290px"
        : "340px",

    maxHeight: isCompactLandscape
      ? "58vh"
      : "70vh",

    transform: isCompactLandscape
      ? "scale(0.92)"
      : "scale(1)",

    transformOrigin: "bottom right",

    overflowY: "auto" as const,
    overflowX: "hidden" as const,
    WebkitOverflowScrolling: "touch" as const,
  };

  return (
    <div
      ref={orbWrapRef}
      onMouseEnter={clearInactiveTimer}
      onMouseLeave={() => {
        if (open) startInactiveTimer();
      }}
      onMouseMove={clearInactiveTimer}
      onTouchStart={clearInactiveTimer}
      onTouchEnd={() => {
        if (open) startInactiveTimer();
      }}
    >
      <div
        className="mobile-orb-pulse"
        aria-hidden="true"
        style={{
          left: `${pos.x}px`,
          top: `${pos.y}px`,
          right: "auto",
          bottom: "auto",
        }}
      />
      <button
        className={`mobile-orb
            ${orbSpeaking ? "orb-speaking" : ""}
            ${orbThinking ? "orb-thinking" : ""}
            ${orbListening ? "orb-listening" : ""}
          `}
        onClick={(e) => { clearInactiveTimer(); handleOrbClick(); }}
        onMouseDown={(e) =>
        startDrag(e.clientX, e.clientY)}
        onTouchStart={(e) => {
          const t = e.touches[0];
          if (!t) return;
          startDrag(t.clientX, t.clientY);
        }}
        aria-label={interfaceCopy.mobile.openControls}
        type="button"
        style={{
          left: `${pos.x}px`,
          top: `${pos.y}px`,
          right: "auto",
          bottom: "auto",
        }}
      >
          {showOrbDiscoveryHint && (
            <div
              style={{
                position: "absolute",
                right: "calc(100% + 14px)",
                top: "50%",
                transform: "translateY(-50%)",
                whiteSpace: "nowrap",
                padding: "8px 12px",
                borderRadius: 999,
                border: "1px solid var(--ava-border)",
                background: "rgba(3,10,16,0.94)",
                color: "var(--ava-face-accent)",
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.02em",
                boxShadow: "0 0 18px var(--ava-accent-glow)",
                pointerEvents: "none",
              }}
            >
              Tap for Menu &amp; Settings
            </div>
          )}

          <div className="orb-core" />

          <div className="orb-ring orb-ring-1" />
          <div className="orb-ring orb-ring-2" />
          <div className="orb-ring orb-ring-3" />

          <div className="orb-particles">
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>

          <div className="orb-center-glow" />
        </button>

      {open && (
        <div
          className="mobile-orb-menu"
          onMouseEnter={clearInactiveTimer}
          onMouseMove={clearInactiveTimer}
          onTouchStart={clearInactiveTimer}
          role="menu"
          aria-label={interfaceCopy.mobile.controls}
          style={menuStyle}
        >
          <button
            type="button"
            onClick={(e) => runOrbAction(e, goProfile)}
          >
            {interfaceCopy.mobile.profile}
          </button>

          <button
            type="button"
            onClick={() => {
              window.dispatchEvent(
                new CustomEvent("ava-open-history")
              );
              closeMenu();
            }}
          >
            {interfaceCopy.mobile.history}
          </button>

          <button
            type="button"
            onClick={() => {
              if (isLockedFreeUser) {
                lockNotice();
                return;
              }
              window.location.href = "/settings";
              closeMenu();
            }}
            style={isLockedFreeUser ? { opacity: 0.45 } : undefined}
          >
            {interfaceCopy.mobile.system}
          </button>
            <button
              type="button"
              className={`theme-toggle-btn ${themeOpen ? "is-open" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clearInactiveTimer();

                if (isFreePlanUser) {
                  lockNotice();
                  return;
                }

                setThemeOpen((prev) => !prev);
              }}
              aria-expanded={themeOpen}
              style={isFreePlanUser ? { opacity: 0.45 } : undefined}
            >
              Theme Color
            </button>

          <button
            type="button"
            onClick={(e) => runOrbAction(e, isFreePlanUser ? lockNotice : toggleOrbEye)}
            aria-pressed={orbEyeOpen}
            title={
              isFreePlanUser
                ? interfaceCopy.mobile.visionLockedTitle
                : orbEyeOpen
                  ? interfaceCopy.mobile.disableVision
                    : interfaceCopy.mobile.enableVision
            }
            className="orb-menu-btn"
            style={isFreePlanUser ? { opacity: 0.55 } : isLockedFreeUser ? { opacity: 0.45 } : undefined}
          >
            {
                isFreePlanUser
                  ? interfaceCopy.mobile.visionLocked
                  : orbEyeOpen
                    ? interfaceCopy.mobile.visionOn
                    : interfaceCopy.mobile.visionOff
              }
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              clearInactiveTimer();

              if (!motionTrackingAllowed) {
                window.alert(
                  interfaceCopy.mobile.motionPaidAlert
                );
                return;
              }

              void toggleOrbMotionTrack();
            }}
            aria-pressed={orbMotionEnabled}
            title={
              !motionTrackingAllowed
                ? interfaceCopy.mobile.motionPaidTitle
                : orbMotionEnabled
                  ? interfaceCopy.mobile.disableMotion
                  : interfaceCopy.mobile.enableMotion
            }
            className="orb-menu-btn"
            style={
              !motionTrackingAllowed
                ? { opacity: 0.55 }
                : undefined
            }
          >
            {!motionTrackingAllowed
              ? interfaceCopy.mobile.motionLocked
              : orbMotionEnabled
                ? interfaceCopy.mobile.motionOn
                : interfaceCopy.mobile.motionOff}
          </button>

                                {themeOpen && !isFreePlanUser && (
              <div className="mobile-orb-theme-panel">
                <div className="mobile-orb-theme-title">
                  Choose Theme
                </div>

                <div className="mobile-orb-theme-grid">
                  {THEME_ORDER.map((item) => (
                    <button
                      key={item}
                      type="button"
                      className={`mobile-theme-swatch ${theme === item ? "active" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        selectTheme(item);
                      }}
                      title={themeLabel(item)}
                      aria-label={`Use ${themeLabel(item)} theme`}
                    >
                      <span className={`mobile-theme-dot theme-${item}`} />
                      <span className="mobile-theme-name">
                        {themeLabel(item)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

          <button
            type="button"
            onClick={() => {
              if (isLockedFreeUser) {
                lockNotice();
                return;
              }
              closeMenu();
              setOpen(false);
              setThemeOpen(false);
              clearInactiveTimer();

              // Smooth logout: let the orb menu close before dashboard redirects.
              window.requestAnimationFrame(() => {
                emit("ava-mobile-logout");
              });
            }}
            style={isLockedFreeUser ? { opacity: 0.45 } : undefined}
          >
            {interfaceCopy.mobile.logout}
          </button>
        </div>
      )}
    </div>
  );
}
